/**
 * SEO grafikleri icin saf geometri. Ekran bilgisi, DOM ve React yok: ayni girdi ayni sayiyi verir, API testi dogrudan koşturur.
 * Sira ekseni TERS: sira 1 ustte (iyi), buyuk sira altta (kotu).
 */
export interface Alan { genislik: number; yukseklik: number; sol: number; sag: number; ust: number; alt: number }
export const ALAN: Alan = { genislik: 640, yukseklik: 260, sol: 44, sag: 16, ust: 16, alt: 36 };

export interface Eksen { min: number; max: number; ticks: number[] }

/** Degerlerden (ve varsa hedeften) sira ekseni. En az bir deger yoksa null: grafik cizilmez. */
export function siraEkseni(degerler: (number | null)[], hedef: number | null = null): Eksen | null {
  const v = degerler.filter((x): x is number => x !== null && Number.isFinite(x));
  if (hedef !== null) v.push(hedef);
  if (v.length === 0) return null;
  let min = Math.floor(Math.min(...v));
  let max = Math.ceil(Math.max(...v));
  if (min === max) { min = Math.max(1, min - 2); max += 2; }
  const pay = Math.max(1, Math.round((max - min) * 0.1));
  min = Math.max(1, min - pay);
  max += pay;
  const adim = Math.max(1, Math.ceil((max - min) / 4));
  const ticks: number[] = [];
  for (let t = min; t <= max; t += adim) ticks.push(t);
  if (ticks[ticks.length - 1] !== max) { ticks.push(max); }
  return { min, max, ticks };
}

export function yKonum(deger: number, e: Eksen, a: Alan = ALAN): number {
  const h = a.yukseklik - a.ust - a.alt;
  return a.ust + ((deger - e.min) / (e.max - e.min)) * h;
}
export function xKonum(i: number, adet: number, a: Alan = ALAN): number {
  const w = a.genislik - a.sol - a.sag;
  return adet <= 1 ? a.sol + w / 2 : a.sol + (i / (adet - 1)) * w;
}

/** Bos (null) noktada cizgi kopar. Yalniz tek basina kalan nokta cizgi degil nokta olarak doner. */
export function cizgiYolu(degerler: (number | null)[], e: Eksen, a: Alan = ALAN): { d: string; noktalar: { x: number; y: number; i: number; deger: number }[] } {
  const adet = degerler.length;
  let d = '';
  let acik = false;
  const noktalar: { x: number; y: number; i: number; deger: number }[] = [];
  degerler.forEach((v, i) => {
    if (v === null) { acik = false; return; }
    const x = Math.round(xKonum(i, adet, a) * 10) / 10;
    const y = Math.round(yKonum(v, e, a) * 10) / 10;
    d += `${acik ? 'L' : 'M'}${x} ${y} `;
    acik = true;
    noktalar.push({ x, y, i, deger: v });
  });
  return { d: d.trim(), noktalar };
}

/** Pozitif tutar sutunlari icin 0 tabanli normal eksen (gosterim). */
export function tutarEkseni(degerler: (number | null)[]): { max: number; ticks: number[] } | null {
  const v = degerler.filter((x): x is number => x !== null);
  if (v.length === 0) return null;
  const tepe = Math.max(...v, 1);
  const kuvvet = 10 ** Math.floor(Math.log10(tepe));
  const adim = [1, 2, 2.5, 5, 10].map((k) => k * kuvvet).find((s) => tepe / s <= 4) ?? 10 * kuvvet;
  const max = Math.ceil(tepe / adim) * adim;
  const ticks: number[] = [];
  for (let t = 0; t <= max; t += adim) ticks.push(t);
  return { max, ticks };
}
export function tutarY(deger: number, max: number, a: Alan = ALAN): number {
  const h = a.yukseklik - a.ust - a.alt;
  return a.ust + h - (deger / max) * h;
}

/** Dagilim yuzdeleri: toplam 0 ise hepsi 0 (bolme yok). */
export function paylar(adetler: number[]): number[] {
  const t = adetler.reduce((a, b) => a + b, 0);
  return adetler.map((n) => (t === 0 ? 0 : (n / t) * 100));
}
